import React from 'react';
import { useI18n } from './i18n';
import { VERDICTS } from './codes';
import { normalizeVariant } from './onavVariants';

/**
 * Official ONAV sheet — "Combined hundred-point and free quantitative parameters method".
 * Faithful reproduction of the paper ONAV sheet (reddish background, two-column layout).
 *
 * LEFT:  score grid (Excellent -> Negative) + defect columns
 * RIGHT: evolution forecast
 */

const W = 900;
const H_BASE = 500;                 // height of the still sheet (14 rows); the sparkling sheet has one more row
const WINE_BG = '#f0ddd5';
const WINE_DARK = '#8b3a3a';
const WINE_MID = '#b56b6b';
const WINE_LIGHT = '#d4a8a8';

// ── Grid parameters (left side), per variant (see onavVariants.js) ──
const STILL_SECTIONS = [
  { labelKey: 'sight', params: [{ key: 'visual_clarity', max: 6 }, { key: 'visual_hue', max: 6 }, { key: 'visual_intensity', max: 6 }] },
  {
    labelKey: 'smell', params: [
      { key: 'olfactory_cleanness', max: 6 }, { key: 'olfactory_intensity', max: 8 }, { key: 'olfactory_finesse', max: 8 },
      { key: 'olfactory_complexity', max: 8, nameKey: 'onav.chart.p.olfactory_complexity' },
    ]
  },
  { labelKey: 'taste', params: [{ key: 'gustatory_cleanness', max: 6 }, { key: 'gustatory_intensity', max: 8 }, { key: 'gustatory_body', max: 8 }] },
  { labelKey: 'taste_smell', params: [{ key: 'gustatory_harmony', max: 8 }, { key: 'gustatory_persistence', max: 8 }, { key: 'gustatory_aftertaste', max: 6 }] },
  { labelKey: '', params: [{ key: 'overall_impression', max: 8 }] },
];
const SPARKLING_SECTIONS = [
  {
    labelKey: 'sight', params: [
      { key: 'visual_clarity', max: 6 }, { key: 'perlage_size', max: 6 }, { key: 'perlage_persistence', max: 6 },
      { key: 'visual_hue', max: 6 }, { key: 'visual_intensity', max: 6 },
    ]
  },
  {
    labelKey: 'smell', params: [
      { key: 'olfactory_cleanness', max: 7 }, { key: 'olfactory_intensity', max: 7 }, { key: 'olfactory_finesse', max: 7 },
      { key: 'olfactory_complexity', max: 7, nameKey: 'onav.chart.p.olfactory_complexity' },
    ]
  },
  { labelKey: 'taste', params: [{ key: 'gustatory_cleanness', max: 7 }, { key: 'gustatory_intensity', max: 7 }, { key: 'gustatory_body', max: 7 }] },
  { labelKey: 'taste_smell', params: [{ key: 'gustatory_harmony', max: 7 }, { key: 'gustatory_persistence', max: 7 }] },
  { labelKey: '', params: [{ key: 'overall_impression', max: 7 }] },
];

const COLS_6 = [6, 5, 4, 3, 2, 1, 0];
const COLS_7 = [7, 6, 5, 4, 3, 2, 0];
const COLS_8 = [8, 7, 6, 5, 4, 2, 0];
const colsFor = (max) => (max === 6 ? COLS_6 : max === 7 ? COLS_7 : COLS_8);     // the printed values of a row of the official forms

const COL_HEADERS = VERDICTS.map((v) => v.code);   // Excellent -> Negative (column order)
const DEFECT_COLS = ['not_matching', 'excess', 'deficiency', 'imbalance'];

// Positions
const LEFT_X = 10;
const GRID_LABEL_W = 80;
const PARAM_LABEL_W = 110;
const CELL_W = 32;
const CELL_H = 22;
const GRID_X = LEFT_X + GRID_LABEL_W + PARAM_LABEL_W;
const NUM_COLS = 7;
const DEFECTS_X = GRID_X + NUM_COLS * CELL_W + 4;
// Vertical evolution forecast: narrow strip on the right edge
const PE_CX = W - 30;
const PE_TOP_Y = 100;
const PE_STEP = 17;
const PE_NUM = 21; // -10..+10

function OnavChart({ data }) {
  const { t, tc, formatDate } = useI18n();
  if (!data) data = {};
  const variant = normalizeVariant(data.variant);
  const GRID_SECTIONS = variant === 'sparkling' ? SPARKLING_SECTIONS : STILL_SECTIONS;
  const rowCount = GRID_SECTIONS.reduce((n, sec) => n + sec.params.length, 0);
  const H = H_BASE + Math.max(0, rowCount - 14) * CELL_H;       // one more row on the sparkling sheet
  const els = [];

  // ═══ BACKGROUND ═══
  els.push(<rect key="bg" x={0} y={0} width={W} height={H} fill={WINE_BG} rx={6} />);

  // ═══ HEADER ═══
  els.push(
    <text key="h1" x={W / 2} y={28} textAnchor="middle" fontSize="18" fontWeight="800" fill={WINE_DARK} fontFamily="serif">
      {t('onav.chart.title')}
    </text>,
    <text key="h2" x={W / 2} y={44} textAnchor="middle" fontSize="8" fill={WINE_MID}>
      {t('onav.chart.subtitle')}
    </text>,
    <text key="h3" x={10} y={62} fontSize="8" fill={WINE_MID}>
      {t('onav.chart.taster')} {data.taster || '___'}
    </text>,
    <text key="h4" x={250} y={62} fontSize="8" fill={WINE_MID}>
      {t('onav.chart.wine')} {data.wine_name || '___'} {data.vintage || ''}
    </text>,
    <text key="h5" x={W - 10} y={62} textAnchor="end" fontSize="8" fill={WINE_MID}>
      {t('onav.chart.date')} {formatDate(data.tasting_date) || '___'}
    </text>,
    <text key="h6" x={W - 10} y={28} textAnchor="end" fontSize="9" fontWeight="700" fill={WINE_DARK}>
      {t(`onav.variant.${variant}`).toLocaleUpperCase()}
    </text>,
  );

  // ═══ SEPARATOR ═══
  els.push(<line key="sep" x1={10} y1={70} x2={W - 10} y2={70} stroke={WINE_DARK} strokeWidth={1.5} />);

  // ═══ LEFT SIDE — SCORE GRID ═══
  let gy = 78;

  // Column headings (diagonal text anchored next to the grid)
  const headerLabelY = gy + 56;
  for (let i = 0; i < NUM_COLS; i++) {
    const cx = GRID_X + i * CELL_W + CELL_W / 2;
    els.push(
      <text key={`ch-${i}`} x={cx} y={headerLabelY} textAnchor="start" fontSize="10"
        fill={WINE_DARK} fontWeight="700"
        transform={`rotate(-55, ${cx}, ${headerLabelY})`}>
        {tc('verdict', COL_HEADERS[i])}
      </text>
    );
  }

  // "Depreciation X" heading
  els.push(
    <text key="dh" x={DEFECTS_X + 2 * CELL_W} y={gy + 8} textAnchor="middle"
      fontSize="9" fill={WINE_MID} fontWeight="600">{t('onav.chart.depreciation')}</text>
  );
  for (let i = 0; i < 4; i++) {
    const cx = DEFECTS_X + i * CELL_W + CELL_W / 2;
    els.push(
      <text key={`dch-${i}`} x={cx} y={headerLabelY} textAnchor="start" fontSize="10"
        fill={WINE_MID} fontWeight="600"
        transform={`rotate(-55, ${cx}, ${headerLabelY})`}>
        {t(`onav.chart.defect.${DEFECT_COLS[i]}`)}
      </text>
    );
  }

  // Exam label
  els.push(
    <text key="el" x={LEFT_X + GRID_LABEL_W / 2} y={gy + 50} textAnchor="middle"
      fontSize="11" fill={WINE_DARK} fontWeight="700" fontStyle="italic">{t('onav.chart.exam')}</text>
  );

  gy += 62;

  // Parameter rows
  for (const [si, sec] of GRID_SECTIONS.entries()) {
    const numRows = sec.params.length;
    const secLabel = sec.labelKey ? t(`onav.chart.sec.${sec.labelKey}`) : '';
    const secH = numRows * CELL_H;

    // Section label (vertical)
    if (secLabel) {
      const lines = secLabel.split('\n');
      const midY = gy + secH / 2;
      lines.forEach((line, li) => {
        els.push(
          <text key={`sl-${si}-${li}`}
            x={LEFT_X + GRID_LABEL_W / 2} y={midY + (li - (lines.length - 1) / 2) * 12}
            textAnchor="middle" fontSize="10" fontWeight="800" fill={WINE_DARK}>
            {line}
          </text>
        );
      });
    }

    // Section border
    els.push(
      <rect key={`sb-${si}`} x={LEFT_X} y={gy - 1} width={GRID_LABEL_W - 2} height={secH + 1}
        fill="none" stroke={WINE_LIGHT} strokeWidth={0.5} />
    );

    for (let pi = 0; pi < numRows; pi++) {
      const p = sec.params[pi];
      const ry = gy + pi * CELL_H;
      const val = data[p.key] || 0;
      const vals = colsFor(p.max);

      // Parameter name
      els.push(
        <text key={`pn-${p.key}`} x={GRID_X - 4} y={ry + 14}
          textAnchor="end" fontSize="8" fill={WINE_DARK} fontWeight="500" fontStyle="italic">
          {t(p.nameKey || `onav.p.${p.key}`)}
        </text>
      );

      // Score cells
      for (let ci = 0; ci < NUM_COLS; ci++) {
        const cx = GRID_X + ci * CELL_W;
        const v = vals[ci];
        const selected = val === v && val > 0;

        els.push(
          <rect key={`gc-${p.key}-${ci}`} x={cx} y={ry} width={CELL_W - 1} height={CELL_H - 1}
            fill={selected ? WINE_DARK : 'white'} stroke={WINE_LIGHT} strokeWidth={0.5} rx={1} />
        );
        els.push(
          <text key={`gv-${p.key}-${ci}`} x={cx + CELL_W / 2} y={ry + 13}
            textAnchor="middle" fontSize="8" fontWeight={selected ? '800' : '400'}
            fill={selected ? 'white' : WINE_MID}>
            {v}
          </text>
        );
      }

      // Defect cells (filled squares when depreciated)
      for (let di = 0; di < 4; di++) {
        const dx = DEFECTS_X + di * CELL_W;
        els.push(
          <rect key={`dc-${p.key}-${di}`} x={dx + 4} y={ry + 4} width={CELL_W - 10} height={CELL_H - 10}
            fill={WINE_DARK} rx={2} opacity={0.15} />
        );
      }

      // Separator row
      els.push(
        <line key={`hr-${p.key}`} x1={GRID_X} y1={ry + CELL_H} x2={DEFECTS_X + 4 * CELL_W} y2={ry + CELL_H}
          stroke={WINE_LIGHT} strokeWidth={0.3} />
      );
    }

    gy += secH + 2;
  }

  // ── PARTIAL TOTALS ──
  els.push(
    <text key="tp-l" x={LEFT_X + GRID_LABEL_W / 2} y={gy + 12}
      textAnchor="middle" fontSize="8" fontWeight="700" fill={WINE_DARK}>
      {t('onav.chart.partials')}
    </text>,
    <text key="tp-d" x={LEFT_X + GRID_LABEL_W / 2} y={gy + 22}
      textAnchor="middle" fontSize="6" fill={WINE_MID}>{t('onav.chart.tens_units')}</text>
  );

  // Compute the grand total
  let grandTotal = 0;
  for (const sec of GRID_SECTIONS) {
    for (const p of sec.params) grandTotal += (data[p.key] || 0);
  }

  // TOTAL box
  els.push(
    <rect key="tot-box" x={DEFECTS_X - 50} y={gy + 2} width={100} height={24}
      fill={WINE_DARK} rx={4} />,
    <text key="tot-label" x={DEFECTS_X - 45} y={gy + 18}
      fontSize="9" fontWeight="700" fill="white">{t('onav.chart.total')}</text>,
    <text key="tot-val" x={DEFECTS_X + 40} y={gy + 18}
      textAnchor="end" fontSize="12" fontWeight="800" fill="white">{grandTotal}</text>,
  );

  // ═══ RIGHT SIDE — EVOLUTION FORECAST (vertical column) ═══
  // Title (text rotated 90° along the column)
  els.push(
    <text key="pe-t" x={PE_CX - 20} y={PE_TOP_Y + (PE_NUM * PE_STEP) / 2}
      textAnchor="middle" fontSize="10" fontWeight="800" fill={WINE_DARK}
      transform={`rotate(-90, ${PE_CX - 20}, ${PE_TOP_Y + (PE_NUM * PE_STEP) / 2})`}>
      {t('onav.chart.evolution')}
    </text>
  );

  // POSITIVE / NEGATIVE labels above/below the column
  els.push(
    <text key="pe-pos" x={PE_CX} y={PE_TOP_Y - 8} textAnchor="middle"
      fontSize="8" fontWeight="700" fontStyle="italic" fill={WINE_DARK}>{t('onav.chart.positive')}</text>,
    <text key="pe-neg" x={PE_CX} y={PE_TOP_Y + PE_NUM * PE_STEP + 4} textAnchor="middle"
      fontSize="8" fontWeight="700" fontStyle="italic" fill={WINE_DARK}>{t('onav.chart.negative')}</text>
  );

  // Circles from +10 (top) to -10 (bottom)
  for (let c = 10; c >= -10; c--) {
    const cy = PE_TOP_Y + (10 - c) * PE_STEP;
    const isNeg = c < 0;
    const absC = Math.abs(c);
    els.push(
      <circle key={`pe-${c}`} cx={PE_CX} cy={cy} r={8}
        fill={isNeg && absC > 5 ? WINE_DARK : 'none'}
        stroke={WINE_MID} strokeWidth={isNeg ? 1.2 : 0.7} />
    );
    els.push(
      <text key={`pen-${c}`} x={PE_CX} y={cy + 3} textAnchor="middle" fontSize="8"
        fontWeight={absC > 5 ? '700' : '400'}
        fill={isNeg && absC > 5 ? 'white' : WINE_MID}>
        {absC}
      </text>
    );
  }

  // ── FOOTER ──
  els.push(
    <line key="foot-line" x1={10} y1={H - 22} x2={W - 10} y2={H - 22}
      stroke={WINE_LIGHT} strokeWidth={0.5} />,
    <text key="foot" x={10} y={H - 8} fontSize="6" fill={WINE_MID}>
      {t('onav.chart.method')}
    </text>,
    <text key="foot2" x={W - 10} y={H - 8} textAnchor="end" fontSize="6" fill={WINE_MID}>
      {tc('verdict', data.verdict)}  {grandTotal}/100
    </text>
  );

  return (
    <div className="onav-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="onav-chart-svg" style={{ maxWidth: '100%' }}>
        {els}
      </svg>
    </div>
  );
}

export default OnavChart;
